// frontend/src/store.js

import {
  legacy_createStore as createStore,
  combineReducers,
  applyMiddleware,
  compose,
} from "redux";
import { thunk } from "redux-thunk";

import {
  productListReducer,
  productLatestReducer,
  productFeaturedReducer,
  productDetailsReducer,
  productBySlugReducer,
  productResolveReducer,
  productRecommendationsReducer,
} from "./reducers/productReducers";

import {
  userAuthReducer,
  userLoginReducer,
  userRegisterReducer,
  userEmailVerifyReducer,
  userEmailResendReducer,
  userUpdateProfileReducer,
  userEmailChangeRequestReducer,
  userEmailChangeVerifyReducer,
  userForgotPasswordReducer,
  userResetPasswordReducer,
  adminUserListReducer,
  adminUserDetailsReducer,
  adminUserCreateReducer,
  adminUserUpdateReducer,
  adminUserDeleteReducer,
} from "./reducers/userReducers";

import {
  portfolioAdminListReducer,
  portfolioCreateReducer,
  portfolioDeleteReducer,
  portfolioDetailsReducer,
  portfolioListReducer,
  portfolioUpdateReducer,
} from "./reducers/portfolioReducers";

import { imageDeleteReducer, uploadReducer } from "./reducers/uploadReducers";

import {
  creditAdminListReducer,
  creditCreateReducer,
  creditDeleteReducer,
  creditDetailsReducer,
  creditListReducer,
  creditUpdateReducer,
} from "./reducers/creditReducers";

import {
  promoCodeAdminListReducer,
  promoCodeCreateReducer,
  promoCodeDeleteReducer,
  promoCodeDetailsReducer,
  promoCodeUpdateReducer,
} from "./reducers/promoCodeReducers";
import { newsletterAdminListReducer } from "./reducers/newsletterReducers";

import {
  contactContentDetailsReducer,
  contactContentUpdateReducer,
  contactCreateReducer,
  contactDeleteReducer,
  contactDetailsReducer,
  contactListReducer,
  contactUpdateStatusReducer,
} from "./reducers/contactReducers";
import {
  adminChatReducer,
  customerChatReducer,
} from "./reducers/chatReducers";

import {
  adminServiceBookingDeleteReducer,
  adminServiceBookingListReducer,
  adminServiceBookingUpdateReducer,
  adminServiceCreateReducer,
  adminServiceDeleteReducer,
  adminServiceDetailsReducer,
  adminServiceListReducer,
  adminServiceOrderAdjustmentReducer,
  adminServiceOrderDetailsReducer,
  adminServiceOrderListReducer,
  adminServiceOrderMessageReducer,
  adminServiceOrderUpdateReducer,
  adminServiceUpdateReducer,
  serviceBookingCreateReducer,
  serviceListReducer,
  serviceOrderAdjustmentResponseReducer,
  serviceOrderCreateReducer,
  serviceOrderDetailsReducer,
  serviceOrderMessageReducer,
  serviceOrderPaymentReducer,
  serviceOrderQuoteReducer,
} from "./reducers/serviceReducers";

import {
  aboutAdminDetailsReducer,
  aboutDeleteReducer,
  aboutDetailsReducer,
  aboutUpdateReducer,
} from "./reducers/aboutReducers";

import {
  categoryListReducer,
  categoryDetailsReducer,
  adminCategoryListReducer,
  adminCategoryDetailsReducer,
  adminCategoryCreateReducer,
  adminCategoryUpdateReducer,
  adminCategoryDeleteReducer,
} from "./reducers/categoryReducers";

import {
  subcategoryListReducer,
  subcategoryDetailsReducer,
  adminSubcategoryListReducer,
  adminSubcategoryDetailsReducer,
  adminSubcategoryCreateReducer,
  adminSubcategoryUpdateReducer,
  adminSubcategoryDeleteReducer,
} from "./reducers/subcategoryReducers";

import { cartReducer } from "./reducers/cartReducers";

import madeToMeasureReducer from "./reducers/madeToMeasureReducers";
import paymentReducer from "./reducers/paymentReducers";
import returnReducer from "./reducers/returnReducer";
import adminOrderReducer from "./reducers/adminOrderReducer";
import orderReducer from "./reducers/orderReducers";
import {
  adminProductCreateReducer,
  adminProductDeleteReducer,
  adminProductDetailsReducer,
  adminProductListReducer,
  adminProductRestoreReducer,
  adminProductStatusReducer,
  adminProductUnpublishReducer,
  adminProductUpdateReducer,
} from "./reducers/adminReducerForProducts";
import {
  adminJewelryHireCreateReducer,
  adminJewelryHireDeleteReducer,
  adminJewelryHireDetailsReducer,
  adminJewelryHireListReducer,
  adminJewelryHireUpdateReducer,
  jewelryHireDetailsReducer,
  jewelryHireListReducer,
} from "./reducers/jewelryHireReducers";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function canUseBrowserStorage() {
  return typeof window !== "undefined" && Boolean(window.localStorage);
}

function loadStoredUserInfo() {
  if (!canUseBrowserStorage()) return null;

  try {
    const raw = window.localStorage.getItem("userInfo");
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const userId = parsed?.id || parsed?._id;

    return userId ? parsed : null;
  } catch {
    return null;
  }
}

function saveStoredUserInfo(userInfo = null) {
  if (!canUseBrowserStorage()) return;

  try {
    const userId = userInfo?.id || userInfo?._id;

    if (userId) {
      window.localStorage.setItem("userInfo", JSON.stringify(userInfo));
    } else {
      window.localStorage.removeItem("userInfo");
    }
  } catch {
    // Ignore storage errors.
  }
}

function buildUserSnapshot(userInfo = null) {
  if (!userInfo) return "null";

  try {
    return JSON.stringify(userInfo);
  } catch {
    return String(userInfo?._id || userInfo?.id || "null");
  }
}

function buildInitialState() {
  const userInfo = loadStoredUserInfo();
  const userId = userInfo?.id || userInfo?._id;
  const isAuthenticated = Boolean(userId);

  return {
    userAuth: {
      loading: false,
      checked: true,
      userInfo: userInfo || null,
      isAuthenticated,
      error: null,
    },

    userLogin: {
      loading: false,
      userInfo: userInfo || null,
      error: null,
    },
  };
}

/* -------------------------------------------------------------------------- */
/*                                Root reducer                                */
/* -------------------------------------------------------------------------- */

export const rootReducer = combineReducers({
  /* ---------------------------------- Cart --------------------------------- */
  cart: cartReducer,

  /* --------------------------------- Orders -------------------------------- */
  order: orderReducer,
  payment: paymentReducer,
  madeToMeasure: madeToMeasureReducer,
  returns: returnReducer,
  adminOrder: adminOrderReducer,

  /* ---------------------------------- Auth --------------------------------- */
  userAuth: userAuthReducer,
  userLogin: userLoginReducer,
  userRegister: userRegisterReducer,
  userEmailVerify: userEmailVerifyReducer,
  userEmailResend: userEmailResendReducer,
  userUpdateProfile: userUpdateProfileReducer,
  userEmailChangeRequest: userEmailChangeRequestReducer,
  userEmailChangeVerify: userEmailChangeVerifyReducer,
  userForgotPassword: userForgotPasswordReducer,
  userResetPassword: userResetPasswordReducer,

  /* ------------------------------- Admin users ----------------------------- */
  adminUserList: adminUserListReducer,
  adminUserDetails: adminUserDetailsReducer,
  adminUserCreate: adminUserCreateReducer,
  adminUserUpdate: adminUserUpdateReducer,
  adminUserDelete: adminUserDeleteReducer,

  /* -------------------------------- Products ------------------------------- */
  productList: productListReducer,
  productLatest: productLatestReducer,
  productFeatured: productFeaturedReducer,
  productDetails: productDetailsReducer,
  productBySlug: productBySlugReducer,
  productRecommendations: productRecommendationsReducer,
  productResolve: productResolveReducer,

  adminProductList: adminProductListReducer,
  adminProductDetails: adminProductDetailsReducer,
  adminProductCreate: adminProductCreateReducer,
  adminProductUpdate: adminProductUpdateReducer,
  adminProductDelete: adminProductDeleteReducer,
  adminProductStatus: adminProductStatusReducer,
  adminProductUnpublish: adminProductUnpublishReducer,
  adminProductRestore: adminProductRestoreReducer,

  /* -------------------------------- Category ------------------------------- */
  categoryList: categoryListReducer,
  categoryDetails: categoryDetailsReducer,
  adminCategoryList: adminCategoryListReducer,
  adminCategoryDetails: adminCategoryDetailsReducer,
  adminCategoryCreate: adminCategoryCreateReducer,
  adminCategoryUpdate: adminCategoryUpdateReducer,
  adminCategoryDelete: adminCategoryDeleteReducer,

  /* ------------------------------- Subcategory ----------------------------- */
  subcategoryList: subcategoryListReducer,
  subcategoryDetails: subcategoryDetailsReducer,
  adminSubcategoryList: adminSubcategoryListReducer,
  adminSubcategoryDetails: adminSubcategoryDetailsReducer,
  adminSubcategoryCreate: adminSubcategoryCreateReducer,
  adminSubcategoryUpdate: adminSubcategoryUpdateReducer,
  adminSubcategoryDelete: adminSubcategoryDeleteReducer,

  /* ------------------------------ Jewelry hire ----------------------------- */
  jewelryHireList: jewelryHireListReducer,
  jewelryHireDetails: jewelryHireDetailsReducer,
  adminJewelryHireList: adminJewelryHireListReducer,
  adminJewelryHireDetails: adminJewelryHireDetailsReducer,
  adminJewelryHireCreate: adminJewelryHireCreateReducer,
  adminJewelryHireUpdate: adminJewelryHireUpdateReducer,
  adminJewelryHireDelete: adminJewelryHireDeleteReducer,

  /* -------------------------------- Portfolio ------------------------------ */
  portfolioList: portfolioListReducer,
  portfolioAdminList: portfolioAdminListReducer,
  portfolioDetails: portfolioDetailsReducer,
  portfolioCreate: portfolioCreateReducer,
  portfolioUpdate: portfolioUpdateReducer,
  portfolioDelete: portfolioDeleteReducer,

  /* --------------------------------- Images -------------------------------- */
  uploadImage: uploadReducer,
  deleteImage: imageDeleteReducer,

  /* -------------------------------- Credits -------------------------------- */
  creditsList: creditListReducer,
  creditsAdminList: creditAdminListReducer,
  creditsDetails: creditDetailsReducer,
  creditsCreate: creditCreateReducer,
  creditsUpdate: creditUpdateReducer,
  creditsDelete: creditDeleteReducer,

  /* ------------------------------ Promo codes ----------------------------- */
  promoCodeAdminList: promoCodeAdminListReducer,
  promoCodeDetails: promoCodeDetailsReducer,
  promoCodeCreate: promoCodeCreateReducer,
  promoCodeUpdate: promoCodeUpdateReducer,
  promoCodeDelete: promoCodeDeleteReducer,

  /* ------------------------------- Newsletter ----------------------------- */
  newsletterAdminList: newsletterAdminListReducer,

  /* -------------------------------- Contact -------------------------------- */
  contactCreate: contactCreateReducer,
  contactList: contactListReducer,
  contactDetails: contactDetailsReducer,
  contactDelete: contactDeleteReducer,
  contactUpdateStatus: contactUpdateStatusReducer,
  contactContentDetails: contactContentDetailsReducer,
  contactContentUpdate: contactContentUpdateReducer,

  /* ---------------------------------- Chat --------------------------------- */
  customerChat: customerChatReducer,
  adminChat: adminChatReducer,

  /* -------------------------------- Services ------------------------------- */
  serviceList: serviceListReducer,
  serviceBookingCreate: serviceBookingCreateReducer,
  serviceOrderQuote: serviceOrderQuoteReducer,
  serviceOrderCreate: serviceOrderCreateReducer,
  serviceOrderDetails: serviceOrderDetailsReducer,
  serviceOrderPayment: serviceOrderPaymentReducer,
  serviceOrderMessage: serviceOrderMessageReducer,
  serviceOrderAdjustmentResponse: serviceOrderAdjustmentResponseReducer,
  adminServiceList: adminServiceListReducer,
  adminServiceDetails: adminServiceDetailsReducer,
  adminServiceCreate: adminServiceCreateReducer,
  adminServiceUpdate: adminServiceUpdateReducer,
  adminServiceDelete: adminServiceDeleteReducer,
  adminServiceBookingList: adminServiceBookingListReducer,
  adminServiceBookingUpdate: adminServiceBookingUpdateReducer,
  adminServiceBookingDelete: adminServiceBookingDeleteReducer,
  adminServiceOrderList: adminServiceOrderListReducer,
  adminServiceOrderDetails: adminServiceOrderDetailsReducer,
  adminServiceOrderUpdate: adminServiceOrderUpdateReducer,
  adminServiceOrderMessage: adminServiceOrderMessageReducer,
  adminServiceOrderAdjustment: adminServiceOrderAdjustmentReducer,

  /* --------------------------------- About --------------------------------- */
  aboutDetails: aboutDetailsReducer,
  aboutAdminDetails: aboutAdminDetailsReducer,
  aboutUpdate: aboutUpdateReducer,
  aboutDelete: aboutDeleteReducer,
});

/* -------------------------------------------------------------------------- */
/*                              Store setup                                   */
/* -------------------------------------------------------------------------- */

const initialState = buildInitialState();

const composeEnhancer =
  (typeof window !== "undefined" &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
  compose;

const middleware = [thunk];

const store = createStore(
  rootReducer,
  initialState,
  composeEnhancer(applyMiddleware(...middleware)),
);

/* -------------------------------------------------------------------------- */
/*                               Persistence                                  */
/* -------------------------------------------------------------------------- */

let lastPersistedUserSnapshot = buildUserSnapshot(
  initialState.userAuth?.userInfo || null,
);

store.subscribe(() => {
  const state = store.getState();

  const userInfo =
    state?.userAuth?.userInfo || state?.userLogin?.userInfo || null;

  const nextSnapshot = buildUserSnapshot(userInfo);

  if (nextSnapshot === lastPersistedUserSnapshot) {
    return;
  }

  lastPersistedUserSnapshot = nextSnapshot;
  saveStoredUserInfo(userInfo);
});

export default store;
